import React, {useEffect, useRef, useState, useCallback} from 'react'
import './style.scss'

import i18n from 'live/services/i18n';
import debounce from 'lodash.debounce';
import styled from 'styled-components';
import { Event, Message, State } from './types';
import DropdownFilter from './components/DropdownFilter';
import DropdownSort from './components/DropdownSort/index';
import ListCards from './components/ListCards';
import { sort } from './utils/sortUtil';

const calcCardHeight = (data: Event) => {
    const size = data?.values?.length || 0  // CARD SIZE IS COMPOSED OF 26px FOR EACH ITEM IN THE VALUES ARRAY + 58px THAT FORMS THE CARD HEADER + 8px MARGIN BOTTOM
    const marginBottom = 8
    const height = (size * 26) + 58 + marginBottom 
    return height
}

const CardsDrillDown = (props: LiveReactWidgetImplProps<State, Message>): JSX.Element => {

    const { state, widget, height } = props
    
    const [query, setQuery] = useState('')
    const [originalValues, setOriginalValues] = useState(undefined)
    const [values, setValues] = useState([])
    const [srt, setSrt] = useState([])
    const [filters, setFilters] = useState({})
    const [loading, setLoading] = useState(false)
    const [currentUrl, setCurrentUrl] = useState(location.hash.split("/")[0])

    const _search = (query: string): void => {
        setLoading(true)        
        setQuery(query)
        const values = listByFilters(originalValues, filters)
        debouncedSearch(query, values)
    }

    //** query term is saved in localStorage as 'query' item.*/
    const debouncedSearch = useCallback(
        debounce((query, values) => {              
            const valuesFiltered = listByQuery(values, query)
            setValues(valuesFiltered)
            localStorage.setItem("query", query)
            setLoading(false)
        }, 500),
        []
    )

    const listByQuery = (values: any[], query:string) => {
        if(!query || query.length === 0) return values

        return values?.filter((value: Event) => {
            const {title = '', values} = value
            const queryUpper = query.toString().toLocaleUpperCase()
            let ruleA = title.toString().toLocaleUpperCase().includes(queryUpper);
            let ruleB = values.find(({
                                         value = '',
                                         searchable
                                     }) => searchable && value.toLocaleUpperCase().includes(queryUpper)) != undefined

            return ruleA || ruleB
        })
    }

    const listByFilters = (values: any[], filters = {}): any[] => {

        let aux = values

        if(Object.keys(filters).length > 0) {
            aux = values?.filter((value:Event) => {
                let result = false
                
                for(let key in filters){
                        
                    const filtersValues = filters[key].map(ele => ele.value)
                    const hasVal = filtersValues.find(val => value[key] === val)
                    
                    if(hasVal) {
                        result = true
                        break;
                    }
                }
    
                return result
            }) 
        }

        return aux
    }

    const applyFilters = (values: any[], filters = {}, query?: string) => {
        let aux = listByFilters(values, filters)
         
        setValues(listByQuery(aux, query))
        setFilters(filters)
        localStorage.setItem("filters", JSON.stringify(filters))
    }

    const onClickSort = (itemSort) => {         
        const array = sort(values, itemSort)
        setSrt(array)
        setValues([])
    }



    useEffect(() => {         
        setValues(srt)
    }, [srt])

    useEffect(() => {            
        setValues(state.data)
        setOriginalValues(state.data)   
        
        if(state.data?.length > 0){            
            const lsQuery = localStorage.getItem("query") || ''       
            let lsFilters = localStorage.getItem("filters") || "{}"
            
            if(lsFilters) lsFilters = JSON.parse(lsFilters)
            
            applyFilters(state.data, lsFilters, lsQuery)
            setQuery(lsQuery)
        }

    }, [state])

    useEffect(() => {
        const handleUrlChange = () => {
            const newUrl = location.hash.split("/")[0];
            if (newUrl !== currentUrl) {
                
                setCurrentUrl(newUrl);
                localStorage.removeItem("query")
                localStorage.removeItem("filters")
            }
        }
        
        window.addEventListener('popstate', handleUrlChange);
        
        return () => {
          window.removeEventListener('popstate', handleUrlChange);
        };
      }, [currentUrl]);

    return (
        <Container>
             <ContainerActions>
                <SearchContainer className="item">
                    <WrapperSearch>
                        <div className="inputgroup">
                            <span className="innericon">
                                <i className="far fa-search"></i>
                            </span>

                            <input
                                name="search"
                                value={query}
                                type="text"
                                className="form-control"
                                placeholder={i18n(state.metaParams?.searchPlaceholder)}
                                onChange={(e) => _search(e?.target?.value)}
                            />

                            <Sufix>
                                <DropdownSort options={state.metaParams?.itemsSort} onClick={onClickSort}/>
                            </Sufix>
                        </div>
                    </WrapperSearch>
                </SearchContainer>

                 {state?.metaParams?.filters.length > 0 && (
                     <DropdownFilter apply={(filters) => applyFilters(originalValues, filters, query)}
                                     values={originalValues}
                                     filters={state.metaParams?.filters} optionsInitialApplied={filters}/>
                 )}

                   
            </ContainerActions> 

            <ResultTrips>
                <span>{i18n('Showing')}</span>
                <span style={{marginInline: 4, fontWeight: 600}}>{values?.length || 0}</span>
                <span style={{textTransform: 'lowercase'}}>{i18n('Trips')}</span>
            </ResultTrips>
        
            <ListCards values={values} cardHeight={calcCardHeight(values?.[0])} qualifier={widget?.jsonConfig?.extensionQualifier} loading={loading}/>
        </Container>
    )
}

export default CardsDrillDown

const Container = styled.div`
    
`


const ContainerActions = styled.div`
    display: flex;
    padding: 0 10px;
    margin-top: 8px;
    align-items: center;    
`

const SearchContainer = styled.div`
    flex: 9;
`

const ResultTrips = styled.div`
    font-weight: 400;
    font-size: 11px;
    padding: 8px 10px;
    color: ${({theme}) => theme['gray-600']};
`

const WrapperSearch = styled.div`
    margin-right: 8px;
    
    .inputgroup {
        display: flex;
        line-height: 26px;
        position: relative;
    }

    input {
        padding-left: 26px;
        border-right: 0;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    .inputgroup .innericon {
        position: absolute;
        top: 4px;
        left: 8px;
        z-index: 2;
        display: block;
        text-align: center;
        pointer-events: none;
        color: ${({ theme }) => theme['gray-600']};
        font-size: 13;
    }
`

const Sufix = styled.div`    
    border-radius: 0px 2px 2px 0px;
    border: 1px solid ${({theme}) => theme['gray-400']};
`